import { useEffect, useRef, useState } from "react";
import { api, ApiError, json, type SessionUser } from "../api";
import { Logo } from "../components/Logo";
import { Link } from "../router";

type State = "detecting" | "ready" | "missing" | "connecting" | "connected" | "failed";

/**
 * /connect/extension: gives the ccnote browser extension an API token. The extension's content script
 * (only on this page) announces itself with "ccnote:extension-ready" and receives the token by
 * window.postMessage, restricted to this origin.
 */
export function ConnectExtension({ user }: { user: SessionUser }) {
  const [state, setState] = useState<State>("detecting");
  const [error, setError] = useState<string | null>(null);
  const ack = useRef<((ok: boolean) => void) | undefined>(undefined);

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.source !== window || event.origin !== window.location.origin) return;
      const type = (event.data as { type?: string } | null)?.type;
      if (type === "ccnote:extension-ready") setState((s) => (s === "detecting" || s === "missing" ? "ready" : s));
      if (type === "ccnote:extension-connected") ack.current?.(true);
      if (type === "ccnote:extension-failed") ack.current?.(false);
    };
    window.addEventListener("message", onMessage);
    // Ask the extension to announce itself (it may have announced before this page was listening).
    const ping = () => window.postMessage({ type: "ccnote:extension-ping" }, window.location.origin);
    ping();
    const pinger = setInterval(ping, 300);
    const timer = setTimeout(() => {
      clearInterval(pinger);
      setState((s) => (s === "detecting" ? "missing" : s));
    }, 3000);
    return () => {
      window.removeEventListener("message", onMessage);
      clearInterval(pinger);
      clearTimeout(timer);
    };
  }, []);

  async function connect() {
    setState("connecting");
    setError(null);
    try {
      const { token } = await api<{ token: string }>("/v1/tokens", { method: "POST", body: json({ name: `Browser extension (${browserName()})` }) });
      const ok = await new Promise<boolean>((resolve) => {
        ack.current = resolve;
        window.postMessage({ type: "ccnote:extension-token", token }, window.location.origin);
        setTimeout(() => resolve(false), 5000);
      });
      setState(ok ? "connected" : "failed");
      if (!ok) setError("The extension didn't answer. Reload this page and try again.");
    } catch (err) {
      setState("failed");
      setError(err instanceof ApiError ? err.message : "Something went wrong.");
    }
  }

  return (
    <main className="auth-page">
      <div className="auth-card">
        <Logo size={40} />
        {state === "connected" ? (
          <>
            <h1>Extension connected ✓</h1>
            <p className="muted">
              Saves go to <strong>{user.address}</strong>. You can close this tab. Revoke access any time in{" "}
              <Link to="/settings">Settings → API tokens</Link>.
            </p>
          </>
        ) : (
          <>
            <h1>Connect the browser extension</h1>
            <p className="muted">
              The ccnote extension saves web pages (text and a full-page screenshot) and X posts to <strong>{user.address}</strong>.
            </p>
            {state === "missing" && (
              <p className="alert">
                The ccnote extension isn't responding on this page. If you just installed or reloaded it, <strong>reload this page</strong>
                (extensions only start on pages opened after they're loaded).
              </p>
            )}
            {error && <p className="alert">{error}</p>}
            <button className="btn btn-primary" onClick={connect} disabled={state === "connecting" || state === "detecting" || state === "missing"}>
              {state === "connecting" ? "Connecting…" : "Connect extension"}
            </button>
          </>
        )}
      </div>
    </main>
  );
}

function browserName(): string {
  const ua = navigator.userAgent;
  const b = /Edg\//.test(ua) ? "Edge" : /Firefox\//.test(ua) ? "Firefox" : /Chrome\//.test(ua) ? "Chrome" : /Safari\//.test(ua) ? "Safari" : "Browser";
  const os = /Mac OS X/.test(ua) ? "Mac" : /Windows/.test(ua) ? "Windows" : /Linux/.test(ua) ? "Linux" : "";
  return os ? `${b}, ${os}` : b;
}
